import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";
import AddBranchMarker from "components/AddBranchMarker";
import { Box, Button, CircularProgress, Snackbar, Stack, TextField } from "@mui/material";
import { updateCompanyDetails, getAreas } from "services/api";
import MDTypography from "components/MDTypography";
import AreaDialog from "components/AddAreaDialog";
import { deleteArea } from "services/api";
import { addArea, updateArea } from "services/api";
import { CloudDownload, CloudUpload } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { exportData } from "services/api";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { detectDataType } from "services/utils";
import { useTranslation } from "react-i18next";
import SnackbarWrapper from "components/Snackbar";
import ConfirmationDialog from "components/ConfirmationDialog";
import ImportButton from "components/ImportButton";

const AreaView = ({ areas, center, setAreas, canEdit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false); // State for dialog visibility
  const [editMode, setEditMode] = useState(false);
  const snackbarRef = useRef();
  const [newArea, setNewArea] = useState({
    areaName: "",
    latitude: null,
    longitude: null,
  });

  const [search, setSearch] = useState("");
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [editLocationArea, setEditLocationArea] = useState(null); //the area object that I want to edit
  const [confirmEditOpen, setConfirmEditOpen] = useState(false); //confirmed to edit location or not
  const [pendingNewLocation, setPendingNewLocation] = useState(null); //temporarily stores the coordinates of new position
  const editMarkerRef = useRef(null); //marker

  // Handle Snackbar close

  const filteredAreas = areas?.filter((area) =>
    area.areaName?.toLowerCase().includes(search?.toLowerCase())
  );

  useEffect(() => {
    if (!dialogOpen) {
      setNewArea({
        areaName: "",
        latitude: null,
        longitude: null,
      });
      setEditMode(false);
    }
  }, [dialogOpen]);

  //show marker when location changes
  useEffect(() => {
    if (editLocationArea && editMarkerRef.current) {
      setTimeout(() => {
        editMarkerRef.current.openPopup();
      }, 0);
    }
  }, [editLocationArea]);

  // Function to handle adding a new branch
  const handleAddArea = (location) => {
    if (!canEdit) return;
    setNewArea((prevBranch) => ({
      ...prevBranch, // Spread the existing branch properties
      latitude: location.latitude, // Update latitude
      longitude: location.longitude, // Update longitude
    }));
    setDialogOpen(true); // Open the dialog
  };

  //After the user clicks on the new map position,confirmation dialog is shown
  const handleEditLocation = async (location) => {
    if (!editLocationArea) return;
    setPendingNewLocation(location);
    setConfirmEditOpen(true);
  };

  // 👉 New handler function
  const handleSaveNewLocation = async () => {
    if (!editLocationArea || !pendingNewLocation) return;

    const updatedArea = { ...editLocationArea, ...pendingNewLocation };
    const res = await updateArea(updatedArea);

    if (res) {
      setAreas(areas.map((a) => (a.areaId === res.areaId ? res : a)));
      setEditLocationArea(null); // exit edit mode
      setPendingNewLocation(null);
    }

    setConfirmEditOpen(false); // close dialog
  };

  // Function to handle form submission
  const handleAdd = async (areaName) => {
    const updatedArea = { ...newArea, areaName: areaName.trim() };
    const nameExist = areas.some(
      (a) =>
        a.areaName?.trim().toLowerCase() === updatedArea.areaName?.toLowerCase() &&
        a.areaId !== updatedArea.areaId // Exclude the area being updated
    );

    if (nameExist) {
      snackbarRef.current?.showSnackbar(t("admin.areas.alreadyExists"), "error");
      setDialogOpen(false);
      return;
    }
    let res = await addArea(updatedArea);

    if (res !== "error") {
      if (editMode) {
        // Replace the existing area with the same areaId
        setAreas(areas.map((a) => (a.areaId === updatedArea.areaId ? res : a)));
      } else {
        // Add new area to the list
        setAreas([...areas, res]);
      }
    }

    setDialogOpen(false);
  };

  const handleEditArea = async (area) => {
    // Check if a branch with the same name already exists (case-insensitive)
    setNewArea(area);
    setEditMode(true);
    setDialogOpen(true); // Open the dialog
  };

  const handleDelete = async (areaId) => {
    let res = await deleteArea(areaId);
    if (res == "success") {
      const updated = areas.filter((area) => area.areaId !== areaId);
      setAreas(updated);
    } else if (res[0] == "C") {
      snackbarRef.current?.showSnackbar(t("admin.areas.cannotDelete"), "error");
      return;
    } else {
      snackbarRef.current?.showSnackbar(t("admin.areas.deleteError"), "error");
      return;
    }

    setDialogOpen(false);
  };

  const handleExport = async () => {
    setDownloadLoading(true);
    await exportData("areas");
    setDownloadLoading(false);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];

    if (!allowedTypes.includes(file.type)) {
      snackbarRef.current?.showSnackbar(t("admin.areas.invalidFileType"), "error");
      return;
    }

    try {
      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = (e) => {
        const binaryString = e.target.result;
        const workbook = XLSX.read(binaryString, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
          defval: "",
        });
        if (data.length === 0) {
          return;
        }

        const columns = Object.keys(data[0]);
        const dataType = detectDataType(columns);
        if (!dataType) {
          snackbarRef.current?.showSnackbar(t("admin.areas.unrecognizedFile"), "error");
          return;
        }

        if (dataType != "areas") {
          snackbarRef.current?.showSnackbar(t("admin.areas.wrongDataType"), "error");
          return;
        }

        // 🎯 Filter only the allowed columns
        const allowedColumns = ["Area ID", "Area Name", "Latitude", "Longitude"];

        const cleanedData = data.map((row) => {
          const cleanedRow = {};
          allowedColumns.forEach((col) => {
            cleanedRow[col] = row[col] ?? "";
          });
          return cleanedRow;
        });
        // Navigate to DataGrid page with parsed data
        navigate("/import", { state: { importedData: cleanedData, dataType } });
      };
    } catch (error) {
      console.error("Error reading file", error);
    } finally {
    }
  };

  const customIcon = new L.Icon({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png", // Default Leaflet marker
    iconSize: [25, 41], // Size of the icon
    iconAnchor: [12, 41], // Positioning of the icon
    popupAnchor: [1, -34], // Positioning of the popup
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    shadowSize: [41, 41],
  });

  const handleImportError = (message, severity) => {
    snackbarRef.current?.showSnackbar(message, severity);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <MDTypography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ display: { xs: "none", sm: "block" } }}
        >
          {t("admin.areas.numberOfAreas")} {areas?.length}
        </MDTypography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 1, sm: 2 }}>
          <MDButton
            color="primary"
            onClick={handleExport}
            startIcon={<CloudDownload />}
            disabled={downloadLoading}
          >
            {downloadLoading ? <CircularProgress size={20} color="inherit" /> : t("export")}
          </MDButton>
          <ImportButton
            // Only allow Jobs to be imported from this page
            allowedTypes={["areas"]}
            // Handle UI feedback
            onError={handleImportError}
            disabled={!canEdit}
          />
        </Stack>
      </Box>

      <TextField
        label={t("admin.areas.searchArea")}
        variant="outlined"
        fullWidth
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ marginBottom: 2 }}
      />

      <MapContainer center={center} zoom={6} style={{ height: "75vh", width: "100%" }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />

        {editLocationArea ? (
          <>
            {/* Edit Location Mode */}
            {/* Show the marker of the area being edited */}
            <Marker
              ref={editMarkerRef}
              key={editLocationArea.areaId}
              position={[editLocationArea.latitude, editLocationArea.longitude]}
              icon={customIcon}
            >
              <Popup autoClose={false} closeOnClick={false} closeButton={false}>
                <Box sx={{ width: 160, textAlign: "center", p: 1, wordWrap: "break-word" }}>
                  <h2 style={{ fontSize: "1rem", margin: "0 0 4px 0", lineHeight: 1.2 }}>
                    {editLocationArea.areaName}
                  </h2>
                  <p style={{ fontSize: "0.85rem", margin: 0, lineHeight: 1.2 }}>
                    {t("admin.areas.click")}
                  </p>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1 }}>
                    <Button
                      color="secondary"
                      onClick={() => setEditLocationArea(null)}
                      sx={{ color: "error.main" }}
                    >
                      {t("admin.employees.delete.cancel")}
                    </Button>
                  </Box>
                </Box>
              </Popup>
            </Marker>

            <AddBranchMarker onAddBranch={handleEditLocation} />
          </>
        ) : (
          //Normal Mode So Show all areas
          filteredAreas?.map((area) => (
            <Marker key={area.areaId} position={[area.latitude, area.longitude]} icon={customIcon}>
              <Popup>
                <Box>
                  <h3 style={{ textAlign: "center", margin: 0 }}>{area.areaName}</h3>
                  <Button
                    disabled={!canEdit}
                    onClick={() => {
                      handleEditArea(area);
                    }}
                  >
                    {t("admin.areas.edit")}
                  </Button>
                  <Button
                    disabled={!canEdit}
                    onClick={() => {
                      setEditLocationArea(area);
                    }}
                  >
                    {t("admin.areas.editLoc")}
                  </Button>
                </Box>
              </Popup>
            </Marker>
          ))
        )}

        {/* <AddBranchMarker onAddBranch={handleAddArea} /> */}
        <AddBranchMarker onAddBranch={editLocationArea ? handleEditLocation : handleAddArea} />
      </MapContainer>

      <ConfirmationDialog
        open={confirmEditOpen}
        onClose={() => setConfirmEditOpen(false)}
        onConfirm={handleSaveNewLocation}
        message={`Are you sure you want to move "${editLocationArea?.areaName}" here?`}
      />

      {/* Dialog for adding a new branch */}
      <AreaDialog
        areas={areas}
        area={newArea}
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        handleDeleteArea={handleDelete}
        handleAddArea={handleAdd}
      />

      {/* Snackbar for Notifications */}
      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </>
  );
};

AreaView.propTypes = {
  areas: PropTypes.array.isRequired,
  setAreas: PropTypes.func.isRequired,
  center: PropTypes.array.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default AreaView;
